<template>
    <v-container fluid class="main-container d-flex flex-column pa-4">
        <v-card class="d-flex flex-column flex-grow-1">
            <v-card-title class="pa-4 d-flex align-center justify-center">
                <v-icon :icon="icons.mdiHexagonMultipleOutline" class="ma-2" color="primary" />
                <span class="font-weight-bold ma-2 text-primary">{{ $t('menu.distributors') }}</span>
                <v-spacer />
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    @click='createDistributor' :prepend-icon="icons.mdiPlus">{{
                        $t('action.newDistributor') }}</v-btn>
            </v-card-title>

            <v-container fluid class="pa-4 fill-height d-flex flex-column flex-grow-1">
                <AgGrid class="flex-grow-1" ref="distributorGrid" :fetchUrl="distributor.fetchUrl" :columnDefs="distributor.columnDefs"
                    :gridParams="distributor.gridParams" :refresh="distributor.refresh"
                    @click-database="onClickDatabase" @click-edit="onClickEdit" />
            </v-container>

        </v-card>

        <!-- Database Dialog -->
        <v-dialog v-model="customer.databaseDialogVisible" class="w-33">
            <v-card>
                <v-card-title class="pa-4">
                    <span class="font-weight-black">{{ $t('dialog.customerDistributorLinks') }} {{ $t('global.for') }} {{ distributor.name }}</span>
                </v-card-title>

                <v-divider />

                <v-container>
                    <AgGrid :fetchUrl="customer.databaseFetchUrl"
                        :columnDefs="customer.databaseColumnDefs" ref="customerDatabaseAgGrid" :gridParams="customer.databaseGridParams"
                        :refresh="customer.databaseRefresh" @on-grid-ready="onGridReady", @on-row-data-updated="onRowDataUpdated"
                        dom-layout="autoHeight"/>
                </v-container>

                <v-divider />

                <v-card-actions class="justify-end">
                    <v-btn variant="text" color="error" @click="onCloseDialog">
                        {{ $t('action.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Create/Edit Distributor Dialog -->
        <v-dialog v-model="distributor.dialogVisible" class="w-33" min-width="560px">
            <v-card class="overflow-visible pt-16">
                <v-form @submit.prevent="saveDistributor" v-model="vformModel">
                    <v-card-item class="w-75 ma-auto overflow-visible pt-14">

                        <v-avatar size="205" color="surface" class="position-absolute dialog-picture elevation-3" border="border-color xl">
                            <v-img v-if="distributor.pictureUrl" :src="distributor.pictureUrl" alt="Cutomer Logo" />
                            <v-icon v-else :icon="icons.mdiAccountMultipleOutline" size="120"  color="primary"/>
                        </v-avatar>
                        <v-btn v-if="false" :icon="icons.mdiCamera" size="small" color="primary" @click="onEditPicture" />

                        <v-text-field v-if="false" v-model="distributor.pictureUrl" :label="$t('field.picture')"
                            :prepend-inner-icon="icons.mdiWeb" variant="outlined" density="comfortable" />

                        <v-text-field v-model="distributor.name" :label="$t('field.name')" class="pt-2" color="primary" icon-color="primary" base-color="primary"
                            :prepend-inner-icon="icons.mdiFormatText" variant="outlined" required
                            :rules="[rules.required]" maxlength="50" counter="50"/>
                    </v-card-item>

                    <v-divider />

                    <v-card-text>
                        <v-container>
                            <v-text-field :prepend-inner-icon="icons.mdiPound" :label="$t('field.maxUsers')" color="primary" icon-color="primary" base-color="primary"
                                v-model="distributor.maxUsers" variant="outlined" required
                                :rules="[rules.required, rules.numericOnly]" :disabled="distributor.editMode"
                                :readonly="distributor.editMode" maxlength="4" :counter="4"/>
                            <v-text-field :prepend-inner-icon="icons.mdiPhone" :label="$t('field.phone')" color="primary" icon-color="primary" base-color="primary" required
                                v-model="distributor.phoneNumber" variant="outlined" maxlength="10" :counter="10" :rules="[rules.required, rules.numericOnly]" />
                            <v-text-field :prepend-inner-icon="icons.mdiAt" :label="$t('field.email')" color="primary" icon-color="primary" base-color="primary" required
                                v-model="distributor.email" variant="outlined" maxlength="50" :counter="50" :rules="[rules.required, rules.email]" />
                            <v-text-field :prepend-inner-icon="icons.mdiHomeOutline" :label="$t('field.address')" color="primary" icon-color="primary" base-color="primary"
                                v-model="distributor.address" variant="outlined" maxlength="300" :counter="300" />
                            <v-select :prepend-inner-icon="icons.mdiWeb" :label="$t('field.country')" color="primary" icon-color="primary" base-color="primary"
                                v-model="distributor.country" variant="outlined" :items="countries" item-title="name" item-value="code" :rules="[rules.required]" required/>
                            <v-text-field :prepend-inner-icon="icons.mdiHomeOutline" :label="$t('field.comment')" color="primary" icon-color="primary" base-color="primary"
                                v-model="distributor.comment" variant="outlined" maxlength="500" :counter="500" />

                        </v-container>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions class="justify-end">
                        <v-btn variant="text" color="error" @click="onCloseDialog" >
                            {{ $t('action.cancel') }}
                        </v-btn>
                        <v-btn variant="text" color="primary" :disabled="!vformModel" type="submit">
                            {{ $t('action.ok') }}
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-container>



</template>

<script setup>
    import { inject, reactive, ref, useTemplateRef, onMounted } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import countriesJson from '../components/json/countries.json';
    import { toast } from 'vue3-toastify';
    import { useControlStore } from '@/stores/controlStore';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';
    import {
        mdiPlus,
        mdiStore,
        mdiHexagonMultipleOutline,
        mdiCamera,
        mdiAccountMultipleOutline,
        mdiPound,
        mdiPhone,
        mdiAt,
        mdiHomeOutline,
        mdiWeb,
        mdiFormatText
    } from '@mdi/js';

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');

    const controlStore = useControlStore();
    const logStore = useLogStore();
    const userStore = useUserStore();

    const icons = {
        mdiPlus,
        mdiStore,
        mdiHexagonMultipleOutline,
        mdiCamera,
        mdiAccountMultipleOutline,
        mdiPound,
        mdiPhone,
        mdiAt,
        mdiHomeOutline,
        mdiWeb,
        mdiFormatText
    };

    const customer = reactive({
        databaseFetchUrl: 'GetCustomers',
        databaseGridParams: {
            token: userStore.getUser().token,
        },
        databaseDialogVisible: false,
        databaseRefresh: false,
        databaseColumnDefs: [
            {
                field: 'name',
                headerName: translate('customer', 'column'),
            },
            {
                field: 'distributorName',
                headerName: translate('distributor', 'column'),
                filter: true,
                filterParams: {
                    readOnly: true
                }
            },
        ],
    });

    const distributors = ref([]);
    const distributor = reactive({
        dialogVisible: false,
        createMode: false,
        editMode: false,
        selected: 0,
        name: '',
        pictureUrl: null,
        maxUsers: null,
        phoneNumber: null,
        email: null,
        address: null,
        comment: null,
        country: null,

        fetchUrl: 'GetDistributors',
        columnDefs: [
            { field: 'name', headerName: translate('name', 'column'), suppressHeaderMenuButton: true },
            { field: 'countryId', headerName: translate('country', 'column'), suppressHeaderMenuButton: true },
            { field: 'maxUsers', headerName: translate('maxUsers', 'column'), suppressHeaderMenuButton: true },
            { field: 'totalUsers', headerName: translate('totalUsers', 'column'), suppressHeaderMenuButton: true },
            { field: 'database', headerName: '', type: 'databaseAction', resizable: true, suppressHeaderMenuButton: true },
            { field: 'edit', headerName: '', type: 'editAction', resizable: true, suppressHeaderMenuButton: true },
        ],
        gridParams: {
            token: userStore.getUser().token,
        },
        refresh: false,
    });
    const distributorGridRef = useTemplateRef('distributorGrid');

    const countries = countriesJson;
    const vformModel = ref(false);
    const rules = {
        required: v => (v !== null && v !== '' && typeof v !== 'undefined') || translate('required', 'toast'),
        max50: v => (v && v.length <= 50) || translate('max50char', 'toast'),
        numericOnly: v => /^[0-9]+$/.test(v) || translate('numOnly', 'toast'),
        email: v => checkEmailValidation(v) || translate('mailAlreadyTaken', 'toast')
    };

    onMounted(() => {
        distributorGridRef.value.getGridData().then(() => {
            distributors.value = distributorGridRef.value.gridRef.api.getGridOption("rowData");
            distributorGridRef.value.gridRef.api.setGridOption("loading", false);
        });  
    });

    function createDistributor() {
        distributor.createMode = true;
        distributor.editMode = false
        distributor.selected = 0;
        distributor.dialogVisible = true;
        distributor.country = null;
    }

    function editDistributor(distributorData) {
        distributor.editMode = true;
        distributor.createMode = false;
        distributor.selected = distributorData.id;

        distributor.name = distributorData.name;
        distributor.maxUsers = distributorData.maxUsers;
        distributor.phoneNumber = distributorData.phoneNumber;
        distributor.email = distributorData.email;
        distributor.address = distributorData.address;
        distributor.country = distributorData.countryId == 1 ? 'FR' : null;
        distributor.comment = distributorData.comment;

        distributor.dialogVisible = true;
    }

    function onEditPicture() {
       //TODO: implement picture edit
    }

    function onClickEdit(distributorData) {
        getDistributorDetail(distributorData);
    }

    function onClickDatabase(distributorData) {
        customer.databaseDialogVisible = true;
        linkCustomerToDistributor(distributorData);
    }

    function linkCustomerToDistributor(distributorsData) {
        distributor.selected = distributorsData.id;
        distributor.name = distributorsData.name;
    }

    function onCloseDialog() {
        distributor.dialogVisible = false;
        customer.databaseDialogVisible = false;
        distributor.selected = 0;
        distributor.name = '';
        distributor.maxUsers = '';
        distributor.phoneNumber = '';
        distributor.email = '';
        distributor.address = '';
        distributor.country = '';
        distributor.comment = '';
    }

    function onGridReady(params) {
        params.api.setGridOption('loading', false);
    }

    function onRowDataUpdated(data) {
        if (!distributorGridRef.value.gridRef.api.isDestroyed()) {
            var modelFilter = {
                distributorName: {
                    type: "set",
                    values: [distributor.name],
                },
            }
            distributorGridRef.value.gridRef.api.setFilterModel(modelFilter);
        }
    }

    function hideDistributorLogo() {
        controlStore.controls.dialogLogoContainer.css('opacity', 0);
    }

    function showDistributorLogo() {
        controlStore.controls.dialogLogoContainer.css('opacity', 1);
    }

    function refreshDistributorGrid() {
        distributor.refresh = true;
        // RESET REFRESH
        setTimeout(() => {
            distributor.refresh = false;
        }, 1000);
    }

    function checkEmailValidation(formValue) {
        if(distributor.editMode && distributor.email == formValue){
            return true;
        }
        else if (distributors.value.filter(d => d.email == formValue).length > 0 && distributors.value.map(d => d.email).indexOf(formValue) > -1) {
            return false;
        }
        else{
            return true;
        }
    }

    // #region DATA

    async function getDistributorDetail(distributorData) {

        let data = {
            token: userStore.getUser().token,
            distributorId: distributorData.id,
        };

        $.getData(urlWebAPI + 'GetDistributorDetail', data)
            .then((data) => {
                editDistributor(data);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetDistributorDetail data.');
            });
    }

    function saveDistributor() {
        let data = {
            token: userStore.getUser().token,
            distributor: {
                id: distributor.selected,
                name: distributor.name,
                maxUsers: distributor.maxUsers,
                phoneNumber: distributor.phoneNumber,
                email: distributor.email,
                address: distributor.address,
                comment: distributor.comment ? distributor.comment : '',
                logoUid: null, 
                countryId: 1, 
            },
        };

        $.postData(urlWebAPI + 'SaveDistributorDetail', data)
            .then((data) => {
                logStore.log(data, 'info');
                toast.success(translate('distributorInfoWellSaved', 'toast'));
                refreshDistributorGrid();
                onCloseDialog();
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveDistributorDetail data.');
            });
    }
// #endregion
</script>
